"use client";

import { type RefObject, useEffect, useMemo, useRef, useState } from "react";
import { buildMediaPlayerWaveformHeights } from "../media-player/media-player-waveform-bars";
import {
	blendWaveformBarHeights,
	configureWaveformAnalyser,
	readFrequencyLevels,
	resampleWaveformLevels,
} from "./waveform-analyser-levels";
import {
	sampleWaveformLevelTimeline,
	type WaveformLevelTimeline,
} from "./waveform-level-timeline";

export {
	blendWaveformBarHeights,
	readFrequencyLevels,
} from "./waveform-analyser-levels";
export type { WaveformLevelTimeline } from "./waveform-level-timeline";
export {
	buildWaveformLevelTimeline,
	decodeAudioBufferFromUrl,
	sampleWaveformLevelTimeline,
} from "./waveform-level-timeline";

const DEFAULT_BAR_COUNT = 28;

interface SilentAnalysisGraph {
	context: AudioContext;
	source: MediaElementAudioSourceNode;
	gainNode: GainNode;
	analyser: AnalyserNode;
	isConnected: boolean;
}

/** Only re-seek analysis on discrete events — not every frame (seeking starves the analyser). */
export const WAVEFORM_ANALYSIS_SYNC_DRIFT_SECONDS = 0.15;

export function syncAnalysisAudioToTransport(
	transport: HTMLAudioElement,
	analysis: HTMLAudioElement,
	force = false,
): void {
	if (!Number.isFinite(transport.currentTime)) {
		return;
	}

	if (
		force ||
		Math.abs(analysis.currentTime - transport.currentTime) >
			WAVEFORM_ANALYSIS_SYNC_DRIFT_SECONDS
	) {
		try {
			analysis.currentTime = transport.currentTime;
		} catch {
			// Ignore seek errors while metadata is loading.
		}
	}
}

function connectSilentAnalysisNodes(graph: SilentAnalysisGraph): void {
	if (graph.isConnected) {
		return;
	}

	graph.gainNode.gain.value = 0;
	graph.source.connect(graph.analyser);
	graph.analyser.connect(graph.gainNode);
	graph.gainNode.connect(graph.context.destination);
	graph.isConnected = true;
}

function disconnectSilentAnalysisNodes(graph: SilentAnalysisGraph): void {
	if (!graph.isConnected) {
		return;
	}

	try {
		graph.source.disconnect();
		graph.analyser.disconnect();
		graph.gainNode.disconnect();
	} catch {
		// Already disconnected.
	}

	graph.isConnected = false;
}

function ensureSilentAnalysisGraph(
	audio: HTMLAudioElement,
): SilentAnalysisGraph | null {
	const existing = analysisGraphByElement.get(audio);
	if (existing) {
		connectSilentAnalysisNodes(existing);
		if (existing.context.state === "suspended") {
			void existing.context.resume();
		}
		return existing;
	}

	try {
		const context = new AudioContext();
		const source = context.createMediaElementSource(audio);
		const gainNode = context.createGain();
		const analyser = context.createAnalyser();
		configureWaveformAnalyser(analyser);

		const graph: SilentAnalysisGraph = {
			context,
			source,
			gainNode,
			analyser,
			isConnected: false,
		};
		analysisGraphByElement.set(audio, graph);
		connectSilentAnalysisNodes(graph);
		void context.resume();
		return graph;
	} catch {
		return null;
	}
}

/** Web Audio graphs keyed by the dedicated (muted) analysis element — never the audible player. */
const analysisGraphByElement = new WeakMap<
	HTMLAudioElement,
	SilentAnalysisGraph
>();

function normalizePlaybackVolume(volumePercent: number): number {
	return Math.max(0, Math.min(1, volumePercent / 100));
}

/** Native element volume for audible playback — never routes through Web Audio. */
export function setMediaElementPlaybackVolume(
	audio: HTMLAudioElement | null | undefined,
	volumePercent: number,
): void {
	if (!audio) {
		return;
	}
	audio.volume = normalizePlaybackVolume(volumePercent);
}

/**
 * Wire a permanently silent analysis graph on a **muted** `<audio>` element.
 * Audible playback must use a separate element with no Web Audio attachment.
 */
export function attachMediaElementAnalyser(
	audio: HTMLAudioElement | null | undefined,
): boolean {
	if (!audio) {
		return false;
	}
	return ensureSilentAnalysisGraph(audio) != null;
}

export function refreshMediaElementAnalyser(
	audio: HTMLAudioElement | null | undefined,
): boolean {
	return attachMediaElementAnalyser(audio);
}

export function hasMediaElementAnalyser(
	audio: HTMLAudioElement | null | undefined,
): boolean {
	if (!audio) {
		return false;
	}
	return analysisGraphByElement.has(audio);
}

/** Keeps graph alive — createMediaElementSource is once-per-element. */
export function detachMediaElementAnalyser(
	audio: HTMLAudioElement | null | undefined,
): void {
	const graph = audio ? analysisGraphByElement.get(audio) : undefined;
	if (!graph) {
		return;
	}

	try {
		disconnectSilentAnalysisNodes(graph);
	} catch {
		// Already disconnected.
	}

	if (graph.context.state === "running") {
		void graph.context.suspend();
	}
}

/** @deprecated Analysis element only — no-op on audible playback element. */
export async function connectMediaElementPlayback(
	audio: HTMLAudioElement | null | undefined,
): Promise<boolean> {
	return attachMediaElementAnalyser(audio);
}

/** @deprecated Analysis element only — no-op on audible playback element. */
export async function disconnectMediaElementPlayback(
	_audio: HTMLAudioElement | null | undefined,
): Promise<void> {}

/** @deprecated Use attachMediaElementAnalyser on the analysis element. */
export function suspendMediaElementAnalyser(
	audio: HTMLAudioElement | null | undefined,
): void {
	detachMediaElementAnalyser(audio);
}

/** @deprecated Use attachMediaElementAnalyser on the analysis element. */
export function resumeMediaElementAnalyser(
	audio: HTMLAudioElement | null | undefined,
): void {
	if (audio) {
		const graph = analysisGraphByElement.get(audio);
		if (graph?.context.state === "suspended") {
			void graph.context.resume();
		}
		attachMediaElementAnalyser(audio);
	}
}

/** @deprecated */
export function prepareMediaElementPlayback(
	audio: HTMLAudioElement | null | undefined,
	volumePercent: number,
): void {
	if (!audio) {
		return;
	}
	audio.muted = true;
	audio.volume = 0;
	attachMediaElementAnalyser(audio);
	setMediaElementPlaybackVolume(audio, volumePercent);
}

/** @deprecated */
export function hasMediaElementAudioGraph(
	audio: HTMLAudioElement | null | undefined,
): boolean {
	return hasMediaElementAnalyser(audio);
}

export function getMediaElementAnalyser(
	audio: HTMLAudioElement | null | undefined,
): AnalyserNode | null {
	const graph = audio ? analysisGraphByElement.get(audio) : undefined;
	if (!graph?.isConnected) {
		return null;
	}
	return graph.analyser;
}

export interface UseMediaElementWaveformOptions {
	/** Muted analysis element wired to the silent Web Audio graph (fallback when no timeline). */
	audioRef: RefObject<HTMLAudioElement | null>;
	/** Audible element — playhead + timeline sampling use this ref when set. */
	transportAudioRef?: RefObject<HTMLAudioElement | null>;
	/** Precomputed levels indexed by transport `currentTime` — preferred for deterministic sync. */
	waveformTimeline?: WaveformLevelTimeline | null;
	enabled: boolean;
	waveformSeed?: string;
	barCount?: number;
	/** Time-based bar animation without audio analysis. */
	mockDancingBars?: boolean;
	/** Allow frequency analysis on the same element used for audible playback. */
	allowSharedTransportAnalyser?: boolean;
	/**
	 * Authoritative playhead seconds (session / fill clock). When set, timeline
	 * sampling uses this instead of `audio.currentTime` so learner chrome stays
	 * synced when the media element lags or is missing.
	 */
	playbackTimeSeconds?: number | null;
}

export interface MediaElementWaveformSnapshot {
	barHeights: number[];
	isLiveWaveform: boolean;
}

export function useMediaElementWaveform({
	audioRef,
	transportAudioRef,
	waveformTimeline = null,
	enabled,
	waveformSeed = "audio-waveform",
	barCount = DEFAULT_BAR_COUNT,
	mockDancingBars = false,
	allowSharedTransportAnalyser = false,
	playbackTimeSeconds = null,
}: UseMediaElementWaveformOptions): MediaElementWaveformSnapshot {
	const baseHeights = useMemo(
		() => buildMediaPlayerWaveformHeights(waveformSeed, barCount),
		[waveformSeed, barCount],
	);
	const [barHeights, setBarHeights] = useState(baseHeights);
	const [isLiveWaveform, setIsLiveWaveform] = useState(false);
	const animationFrameRef = useRef<number>(0);
	const playbackTimeSecondsRef = useRef(playbackTimeSeconds);
	playbackTimeSecondsRef.current = playbackTimeSeconds;

	useEffect(() => {
		setBarHeights(baseHeights);
		setIsLiveWaveform(false);
	}, [baseHeights, barCount]);

	useEffect(() => {
		if (!enabled) {
			cancelAnimationFrame(animationFrameRef.current);
			setIsLiveWaveform(false);
			return;
		}

		const tick = () => {
			const transport = transportAudioRef?.current ?? audioRef.current;
			const analysisAudio = audioRef.current;
			const isPlaying =
				transport != null && !transport.paused && !transport.ended;
			const hasTimeline =
				waveformTimeline != null && waveformTimeline.frames.length > 0;
			const clockFromSession = playbackTimeSecondsRef.current;
			const clockSeconds =
				clockFromSession != null && Number.isFinite(clockFromSession)
					? clockFromSession
					: (transport?.currentTime ?? null);
			const analysisSharesAudibleTransport =
				transport != null &&
				analysisAudio != null &&
				transport === analysisAudio;

			if (hasTimeline && clockSeconds != null) {
				const liveLevels = resampleWaveformLevels(
					sampleWaveformLevelTimeline(
						waveformTimeline,
						clockSeconds,
					),
					barCount,
				);
				setBarHeights(blendWaveformBarHeights(liveLevels, baseHeights));
				setIsLiveWaveform(
					isPlaying || playbackTimeSecondsRef.current != null,
				);
			} else if (
				isPlaying &&
				analysisAudio &&
				(!analysisSharesAudibleTransport ||
					allowSharedTransportAnalyser)
			) {
				if (analysisAudio.paused) {
					void analysisAudio.play().catch(() => undefined);
				}

				if (!analysisGraphByElement.has(analysisAudio)) {
					attachMediaElementAnalyser(analysisAudio);
				}

				const graph = analysisGraphByElement.get(analysisAudio);
				if (graph?.context.state === "suspended") {
					void graph.context.resume();
				}

				const analyser = getMediaElementAnalyser(analysisAudio);
				if (analyser) {
					const liveLevels = readFrequencyLevels(analyser, barCount);
					setBarHeights(
						blendWaveformBarHeights(liveLevels, baseHeights),
					);
					setIsLiveWaveform(true);
				}
			} else if (mockDancingBars) {
				const nowSeconds = performance.now() / 1000;
				const liveLevels = baseHeights.map((height, index) => {
					const wave =
						Math.sin(nowSeconds * 3.2 + index * 0.45) * 0.5 + 0.5;
					const secondary =
						Math.sin(nowSeconds * 5.1 + index * 0.28) * 0.5 + 0.5;
					const blend = wave * 0.65 + secondary * 0.35;
					return height * (0.28 + blend * 0.72);
				});
				setBarHeights(liveLevels);
				setIsLiveWaveform(true);
			} else {
				setIsLiveWaveform(false);
			}

			animationFrameRef.current = requestAnimationFrame(tick);
		};

		animationFrameRef.current = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(animationFrameRef.current);
	}, [
		audioRef,
		barCount,
		baseHeights,
		enabled,
		mockDancingBars,
		allowSharedTransportAnalyser,
		transportAudioRef,
		waveformTimeline,
	]);

	return { barHeights, isLiveWaveform };
}
